import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, LogIn, LogOut, Heart, PenTool, X, Home, Settings, Clock, Download, Building2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { toast } from "sonner";
export const Header = () => {
  const { user, signOut, subscribed, inTrial, trialDaysRemaining } = useAuth();
  const { isInstallable, isIOS, isMobile, promptInstall } = useInstallPrompt();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const closeMenu = () => setIsOpen(false);

  const handleInstallClick = () => {
    if (isIOS) {
      toast.info("Tap the Share button, then 'Add to Home Screen'");
    } else if (isInstallable) {
      promptInstall();
    } else {
      // Android without native prompt support
      toast.info("Tap the menu (⋮), then 'Add to Home Screen' or 'Install App'");
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    toast.success("Signed out successfully");
  };

  const handleManageSubscription = () => {
    window.open('https://billing.stripe.com/p/login/cNi7sM7TFdYC9zP1xx4AU00', '_blank');
  };


  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <span className="font-display text-xl text-primary-foreground">OZ</span>
          </div>
          <div className="hidden sm:block">
            <span className="font-display text-lg text-foreground tracking-wide">
              OZ PERFORMANCE
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/library" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Drills
          </Link>
          <Link to="/designer" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
            <PenTool className="w-4 h-4" />
            Designer
          </Link>
          <Link to="/sessions" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
            <Heart className="w-4 h-4" />
            Sessions
          </Link>
          <Link to="/clubs" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            Clubs
          </Link>
          <Link to="/au-coaches" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
            <GraduationCap className="w-4 h-4" />
            AU Coaches
          </Link>
          {(isInstallable || isMobile) && (
            <button 
              onClick={handleInstallClick}
              className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1 font-medium"
            >
              <Download className="w-4 h-4" />
              Install App
            </button>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {subscribed && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleManageSubscription}
                  className="hidden md:flex"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Subscription
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="hidden md:flex">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </>
          ) : (
            <Link to="/auth" className="hidden md:block">
              <Button variant="ghost" size="sm">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </Link>
          )}
          
          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-background border-border">
              <nav className="flex flex-col gap-4 mt-8">
                <Link 
                  to="/" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Home
                </Link>
                <Link 
                  to="/library" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  <div className="w-5 h-5 rounded bg-primary/20 flex items-center justify-center text-xs text-primary">⚽</div>
                  Drills
                </Link>
                <Link 
                  to="/designer" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  <PenTool className="w-5 h-5" />
                  Designer
                </Link>
                <Link 
                  to="/sessions" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  <Heart className="w-5 h-5" />
                  Sessions
                </Link>
                <Link 
                  to="/clubs" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  <Building2 className="w-5 h-5" />
                  Clubs
                </Link>
                <Link 
                  to="/au-coaches" 
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors"
                >
                  <GraduationCap className="w-5 h-5" />
                  AU Coaches
                </Link>
                
                <div className="border-t border-border my-4" />

                {(isInstallable || isMobile) && (
                  <button 
                    onClick={() => {
                      handleInstallClick();
                      closeMenu();
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-primary hover:bg-muted transition-colors font-medium"
                  >
                    <Download className="w-5 h-5" />
                    Install App
                  </button>
                )}
                
                <div className="border-t border-border my-4" />
                
                {user ? (
                  <>
                    {subscribed && (
                      <Button 
                        variant="ghost" 
                        className="justify-start px-4 py-3 h-auto text-foreground hover:bg-muted w-full"
                        onClick={() => {
                          handleManageSubscription();
                          closeMenu();
                        }}
                      >
                        <Settings className="w-5 h-5 mr-3" />
                        Manage Subscription
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      className="justify-start px-4 py-3 h-auto text-foreground hover:bg-muted"
                      onClick={() => {
                        handleSignOut();
                        closeMenu();
                      }}
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Link to="/auth" onClick={closeMenu}>
                    <Button variant="ghost" className="w-full justify-start px-4 py-3 h-auto text-foreground hover:bg-muted">
                      <LogIn className="w-5 h-5 mr-3" />
                      Sign In
                    </Button>
                  </Link>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
